import { apiClient } from "./apiClient";

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

export interface Permission {
  id: string;
  name: string;
  resource: string;
  action: string;
  description?: string;
  isSystem?: boolean;
}

export interface Role {
  name: string;
  description?: string;
  permissions?: string[];
}

export interface IdentityUser {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  username?: string;
  roles?: string[];
}

export interface Group {
  id: string;
  name: string;
  description?: string;
  legalEntityId?: string;
  roles?: string[];
}

export interface GroupMember {
  id: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  username?: string;
}

function asOptionalString(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  const trimmed = value.trim();
  return trimmed || undefined;
}

/** `/groups/{id}/members` returns `userId` + `name`; normalise to the shape the UI expects. */
function normalizeGroupMember(raw: unknown): GroupMember | null {
  if (!raw || typeof raw !== "object") return null;
  const record = raw as Record<string, unknown>;
  const id = asOptionalString(record.userId) ?? asOptionalString(record.id);
  if (!id) return null;

  const email = asOptionalString(record.email);
  const firstName = asOptionalString(record.firstName);
  const lastName = asOptionalString(record.lastName);
  const username = asOptionalString(record.username);
  const fullName = asOptionalString(record.name);

  if (firstName || lastName) {
    return { id, email, firstName, lastName, username };
  }

  if (fullName) {
    const parts = fullName.trim().split(/\s+/);
    const normalizedFirstName = parts[0];
    const normalizedLastName = parts.slice(1).join(" ");

    return {
      id,
      email,
      username,
      firstName: normalizedFirstName,
      lastName: normalizedLastName || undefined,
    };
  }

  return { id, email, username };
}

export interface MeResponse {
  userSub: string;
  email: string;
  name: string;
  roles: string[];
  permissions: string[];
  /**
   * Branch scoping for the logged-in user. When the user is associated with
   * exactly one branch, `branchId` is that branch (and `legalEntityId` its
   * legal entity) and the UI locks the branch selector to it. Null/undefined
   * when the user is not branch-scoped, so the UI keeps the normal
   * "All Branches" behaviour.
   */
  legalEntityId?: string | null;
  branchId?: string | null;

  /**
   * Every branch this user may see. `branchId` above is the single-element special
   * case; prefer this list, which also scopes users holding several branches.
   */
  allowedBranchIds?: string[] | null;

  /** Legal entities that must render for the allowed branches to be reachable. */
  visibleLegalEntityIds?: string[] | null;

  /** Root legal entities this email can reach — one email may span several organisations. */
  accessibleRootLegalEntityIds?: string[] | null;

  /** Organisation (root legal entity) the rest of /me is scoped to. */
  selectedOrganisationId?: string | null;

  /** True for master users: the id lists above are empty and impose no restriction. */
  unrestricted?: boolean;
}

// ─────────────────────────────────────────────────────────────────────────────
// Service
// ─────────────────────────────────────────────────────────────────────────────

const BASE = "/access";

export const accessService = {
  // ── /me ──
  getMe(): Promise<MeResponse> {
    return apiClient.get<MeResponse>(`${BASE}/me`).then((r) => r.data);
  },

  // ── Permissions ──
  listPermissions(): Promise<Permission[]> {
    return apiClient.get<Permission[]>(`${BASE}/permissions`).then((r) => r.data);
  },
  createPermission(body: { name: string; resource: string; action: string; description?: string }): Promise<Permission> {
    return apiClient.post<Permission>(`${BASE}/permissions`, body).then((r) => r.data);
  },
  updatePermission(id: string, body: { description?: string }): Promise<Permission> {
    return apiClient.patch<Permission>(`${BASE}/permissions/${id}`, body).then((r) => r.data);
  },
  deletePermission(id: string): Promise<void> {
    return apiClient.delete(`${BASE}/permissions/${id}`).then(() => undefined);
  },

  // ── Roles ──
  listRoles(includePermissions = false): Promise<Role[]> {
    return apiClient
      .get<Role[]>(`${BASE}/roles`, { params: { includePermissions } })
      .then((r) => r.data);
  },
  getRole(name: string): Promise<Role> {
    return apiClient.get<Role>(`${BASE}/roles/${name}`).then((r) => r.data);
  },
  createRole(body: { name: string; description?: string; legalEntityId?: string }): Promise<Role> {
    return apiClient.post<Role>(`${BASE}/roles`, body).then((r) => r.data);
  },
  updateRole(name: string, body: { description?: string }): Promise<Role> {
    return apiClient.patch<Role>(`${BASE}/roles/${name}`, body).then((r) => r.data);
  },
  deleteRole(name: string): Promise<void> {
    return apiClient.delete(`${BASE}/roles/${name}`).then(() => undefined);
  },
  getRolePermissions(name: string): Promise<string[]> {
    return apiClient.get<string[]>(`${BASE}/roles/${name}/permissions`).then((r) => r.data);
  },
  setRolePermissions(name: string, permissionNames: string[]): Promise<string[]> {
    return apiClient
      .put<string[]>(`${BASE}/roles/${name}/permissions`, { permissions: permissionNames ?? [] })
      .then((r) => r.data);
  },

  // ── Users ──
  searchUsers(q?: string, max = 20): Promise<IdentityUser[]> {
    return apiClient
      .get<IdentityUser[]>(`${BASE}/users`, { params: { q, max } })
      .then((r) => r.data);
  },
  getUserRoles(userSub: string): Promise<string[]> {
    return apiClient.get<string[]>(`${BASE}/users/${userSub}/roles`).then((r) => r.data);
  },
  assignRole(userSub: string, roleName: string): Promise<{ ok: boolean; requiresTokenRefresh: boolean }> {
    return apiClient
      .post(`${BASE}/users/${userSub}/roles`, { roleName })
      .then((r) => r.data);
  },
  removeRole(userSub: string, roleName: string): Promise<{ ok: boolean; requiresTokenRefresh: boolean }> {
    return apiClient
      .delete(`${BASE}/users/${userSub}/roles/${roleName}`)
      .then((r) => r.data);
  },

  // ── Groups ──
  listGroups(): Promise<Group[]> {
    return apiClient.get<Group[]>("/groups").then((r) => r.data);
  },
  getGroup(id: string): Promise<Group> {
    return apiClient.get<Group>(`/groups/${id}`).then((r) => r.data);
  },
  createGroup(body: { name: string; description?: string; legalEntityId?: string; roles?: string[] }): Promise<Group> {
    return apiClient.post<Group>("/groups", body).then((r) => r.data);
  },
  updateGroup(id: string, body: { name?: string; description?: string }): Promise<Group> {
    return apiClient.patch<Group>(`/groups/${id}`, body).then((r) => r.data);
  },
  deleteGroup(id: string): Promise<void> {
    return apiClient.delete(`/groups/${id}`).then(() => undefined);
  },
  getGroupMembers(id: string): Promise<GroupMember[]> {
    return apiClient.get<unknown>(`/groups/${id}/members`).then((r) => {
      const list = Array.isArray(r.data) ? r.data : [];
      return list
        .map((item) => normalizeGroupMember(item))
        .filter((member): member is GroupMember => member != null);
    });
  },
  addGroupMembers(id: string, userIds: string[]): Promise<void> {
    return apiClient.post(`/groups/${id}/members`, { userIds }).then(() => undefined);
  },
  removeGroupMember(id: string, userId: string): Promise<void> {
    return apiClient.delete(`/groups/${id}/members/${userId}`).then(() => undefined);
  },
  getGroupRoles(id: string): Promise<string[]> {
    return apiClient.get<string[]>(`/groups/${id}/roles`).then((r) => r.data);
  },
  setGroupRoles(id: string, roles: string[]): Promise<void> {
    return apiClient.put(`/groups/${id}/roles`, { roles }).then(() => undefined);
  },
};
